import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ReduxCommandCreator } from 'appRedux/actions';
import { useAppSelector } from 'appRedux/createStore';
import {
  buildSupportedPhoneCountries,
  SupportedPhoneCountry,
  SupportedPhoneCountryConfig,
} from 'common/phoneCountries';
import { AuthService } from 'services/auth';

interface IUseSupportedPhoneCountries {
  supportedPhoneCountries: SupportedPhoneCountry[];
  isLoading: boolean;
}

const PHONE_COUNTRIES_TTL_MS = 24 * 60 * 60 * 1000;

const getLatestCountryCacheTimestamp = (
  countryConfigs?: SupportedPhoneCountryConfig[]
): number => {
  if (!Array.isArray(countryConfigs)) {
    return 0;
  }

  return countryConfigs.reduce<number>((latestTimestamp, countryConfig) => {
    const rawCachedAt: unknown = countryConfig.cachedAt;
    const countryCachedAt = typeof rawCachedAt === 'number' ? rawCachedAt : 0;
    return countryCachedAt > latestTimestamp ? countryCachedAt : latestTimestamp;
  }, 0);
};

export const useSupportedPhoneCountries = (): IUseSupportedPhoneCountries => {
  const dispatch = useDispatch();
  const persistedSupportedPhoneCountries: unknown = useAppSelector(
    (state) => state.app.persistedState.supportedPhoneCountries
  );
  const cachedCountryConfigs: SupportedPhoneCountryConfig[] | undefined = Array.isArray(
    persistedSupportedPhoneCountries
  )
    ? persistedSupportedPhoneCountries.filter((country): country is SupportedPhoneCountryConfig => {
      return typeof country === 'object' && country !== null;
    })
    : undefined;
  const [isLoading, setIsLoading] = useState(typeof cachedCountryConfigs === 'undefined');

  useEffect(() => {
    const latestCachedAt = getLatestCountryCacheTimestamp(cachedCountryConfigs);
    const isCacheFresh =
      typeof cachedCountryConfigs !== 'undefined' &&
      latestCachedAt > 0 &&
      Date.now() - latestCachedAt < PHONE_COUNTRIES_TTL_MS;

    if (isCacheFresh) {
      setIsLoading(false);
      return;
    }

    const commands = ReduxCommandCreator(dispatch);
    const authService = new AuthService();
    const subscription = authService.GetSupportedPhoneCountries().subscribe({
      next: (response) => {
        const fetchedAt = Date.now();
        const countriesWithCacheTime = (response?.response?.countries ?? []).map((country) => ({
          ...country,
          cachedAt: fetchedAt,
        }));
        commands.SetSupportedPhoneCountries(countriesWithCacheTime);
        setIsLoading(false);
      },
      error: () => {
        if (typeof cachedCountryConfigs === 'undefined') {
          commands.SetSupportedPhoneCountries([]);
        }
        setIsLoading(false);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [cachedCountryConfigs, dispatch]);

  return {
    supportedPhoneCountries: buildSupportedPhoneCountries(cachedCountryConfigs),
    isLoading,
  };
};